import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { useLeadDetails } from '../../contexts/LeadDetailsContext';
import PlaceSearchAutocomplete from './PlaceSearchAutocomplete';

const T = {
  green: '#065F46',
  greenLt: '#D1FAE5',
  greenMd: '#059669',
  ink: '#0f1a13',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#F0FDF4',
  borderL: '#A7F3D0',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const LocationUpdate = ({ open, onClose, leadId }) => {
  const { preferences, fetchLeadPreference, updateLeadPreferences, setPreferences } = useLeadDetails();
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);   // list of city-name strings
  const [pickerValue, setPickerValue] = useState(null); // current PlaceSearch selection
  const [saveError, setSaveError] = useState(null);

  // Populate from saved preferences when dialog opens
  useEffect(() => {
    if (!open) return;
    setSaveError(null);
    setPickerValue(null);
    const saved = preferences?.saved_preferences?.location || [];
    setCities(Array.isArray(saved) ? saved.map(String).filter(Boolean) : []);
  }, [open, preferences]);

  const handlePickerChange = (_e, cityObj) => {
    if (!cityObj) return;
    const name = (cityObj.city || '').trim();
    if (!name) return;
    setCities((prev) => {
      if (prev.some((c) => c.toLowerCase() === name.toLowerCase())) return prev;
      return [...prev, name];
    });
    // Reset picker so the user can add another city
    setPickerValue(null);
  };

  const handleRemove = (name) => {
    setCities((prev) => prev.filter((c) => c !== name));
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveError(null);
    try {
      await updateLeadPreferences(leadId, cities, 'location_preferences');

      // Close immediately after successful save — don't block on the refresh
      onClose();

      // Optimistically update context so chips reflect new values immediately
      if (preferences) {
        setPreferences({
          ...preferences,
          saved_preferences: {
            ...preferences.saved_preferences,
            location: cities,
          },
        });
      } else {
        fetchLeadPreference(leadId).catch(() => {});
      }
    } catch (err) {
      setSaveError(err?.message || 'Could not save locations. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 480,
          width: '100%',
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: '10px', bgcolor: T.greenLt,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <PlaceOutlinedIcon sx={{ fontSize: 22, color: T.green }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
                Location preferences
              </Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted, mt: 0.25, lineHeight: 1.45 }}>
                Search and add cities this lead is interested in
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={loading} sx={{ color: T.muted, '&:hover': { color: T.ink } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      {/* ── Body ── */}
      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
        {/* City search picker */}
        <PlaceSearchAutocomplete
          value={pickerValue}
          onChange={handlePickerChange}
          label="Search city / area"
          placeholder="Search for an Indian city..."
          size="small"
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '& fieldset': { borderColor: T.borderL },
              '&:hover fieldset': { borderColor: '#6EE7B7' },
              '&.Mui-focused fieldset': { borderColor: T.green },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: T.green },
          }}
        />

        <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted, mt: 1, lineHeight: 1.5 }}>
          Select a city from the dropdown to add it. Remove with ✕.
        </Typography>

        {/* Selected city chips */}
        {cities.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${T.borderL}`,
              bgcolor: T.bg,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
            }}
          >
            {cities.map((city) => (
              <Chip
                key={city}
                label={city}
                size="small"
                onDelete={() => handleRemove(city)}
                sx={{
                  fontFamily: T.font,
                  fontSize: 12,
                  height: 26,
                  bgcolor: T.greenLt,
                  color: T.green,
                  border: `1px solid #6EE7B7`,
                  '& .MuiChip-deleteIcon': { color: T.muted, '&:hover': { color: T.green } },
                }}
              />
            ))}
          </Box>
        )}

        {saveError && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, border: `1px solid ${T.redBorder}`, bgcolor: T.redLt }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red }}>{saveError}</Typography>
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions
        sx={{
          px: 2.5, py: 2, gap: 1, justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`, bgcolor: T.bg,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ fontFamily: T.font, fontSize: 13, fontWeight: 500, color: T.subtle, textTransform: 'none', borderRadius: 2, px: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{
            fontFamily: T.font, fontSize: 13, fontWeight: 600, textTransform: 'none',
            borderRadius: 2, px: 2.5, minWidth: 132,
            bgcolor: T.green, color: '#fff', boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          {loading ? <CircularProgress size={22} thickness={4} sx={{ color: '#fff' }} /> : 'Save locations'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationUpdate;
